import { useEffect, useState, useCallback } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Web Push (VAPID) opt-in toggle for the driver.
 *
 * - Hidden if the browser doesn't support Notifications / Push / ServiceWorker
 *   (iOS Safari <16.4, some in-app WebViews).
 * - Reflects the browser's actual PushSubscription state; on unsubscribe we
 *   also delete the row on the backend so it doesn't get stale.
 */
function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function supported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export default function PushToggle() {
  const [state, setState] = useState("idle"); // idle | subscribed | denied | unsupported | busy
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!supported()) { setState("unsupported"); return; }
    if (Notification.permission === "denied") { setState("denied"); return; }
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setOk(!!sub);
        setState(sub ? "subscribed" : "idle");
      } catch { /* leave idle */ }
    })();
  }, []);

  const enable = useCallback(async () => {
    if (!supported()) return;
    setState("busy");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState(perm === "denied" ? "denied" : "idle"); return; }
      const { data } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      const j = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
      setOk(true);
      setState("subscribed");
      toast.success("Push notifications enabled");
    } catch (e) {
      setState("idle");
      toast.error("Could not enable notifications. Please try again.");
    }
  }, []);

  const disable = useCallback(async () => {
    if (!supported()) return;
    setState("busy");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try { await api.delete(`/push/subscribe`, { params: { endpoint } }); } catch { /* best-effort */ }
      }
      setOk(false);
      setState("idle");
      toast.success("Push notifications turned off");
    } catch { setState(ok ? "subscribed" : "idle"); }
  }, [ok]);

  if (state === "unsupported") return null;

  const Icon = ok ? Bell : BellOff;
  const label = state === "denied"
    ? "Notifications blocked"
    : state === "busy"
      ? (ok ? "Turning off…" : "Enabling…")
      : ok ? "Notifications on" : "Enable alerts";
  const onClick = state === "denied" ? undefined : ok ? disable : enable;
  const disabled = state === "busy" || state === "denied";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid="push-toggle"
      title={state === "denied" ? "Enable notifications in your browser settings to receive delivery alerts." : ""}
      className={
        "inline-flex items-center gap-2 px-4 py-2.5 text-sm border transition-colors " +
        (ok
          ? "bg-[#0a0a0a] border-[#0a0a0a] text-white"
          : "border-gray-300 text-gray-600 hover:border-[#0a0a0a] disabled:opacity-50")
      }
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}
